import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { login, ApiError } from "../lib/api.js";
import {
  signIn,
  completeNewPassword,
  respondToTotp,
  verifyTotpSetup,
  forgotPassword,
  confirmForgotPassword,
  isCognitoConfigured,
  type SignInResult,
  type CognitoUser,
} from "../lib/cognito.js";
import { ClearableInput } from "./ClearableInput.js";
import { formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Step =
  | { kind: "credentials" }
  | { kind: "new_password"; cognitoUser: CognitoUser; email: string }
  | { kind: "totp";  cognitoUser: CognitoUser }
  | { kind: "setup"; cognitoUser: CognitoUser; secretCode: string; email: string }
  | { kind: "forgot_password" }
  | { kind: "reset_password"; email: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Sign-in failed. Please try again.";
}

// Build an otpauth:// deep-link so mobile users can tap to open an authenticator app.
function totpUri(email: string, secret: string): string {
  const label = encodeURIComponent(`Medical Assistant:${email}`);
  return `otpauth://totp/${label}?secret=${secret}&issuer=Medical+Assistant`;
}

// ---------------------------------------------------------------------------
// Shared UI pieces
// ---------------------------------------------------------------------------

const inputCls =
  "w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 " +
  "px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-500";

const btnPrimary =
  "w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white " +
  "hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50";

const btnGhost =
  "text-xs text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-300";

function Card({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
      <div className={`w-full space-y-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm ${wide ? "max-w-md" : "max-w-sm"}`}>
        {children}
      </div>
    </div>
  );
}

function Heading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const cognito = isCognitoConfigured();
  useDocumentTitle(formatPageTitle("Sign in"));

  // Cognito state
  const [step,     setStep]     = useState<Step>({ kind: "credentials" });
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [code,     setCode]     = useState("");
  const [newPw,    setNewPw]    = useState("");
  const [newPw2,   setNewPw2]   = useState("");
  const [error,    setError]    = useState<string | null>(null);
  const [busy,     setBusy]     = useState(false);

  // Forgot-password state
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode,  setResetCode]  = useState("");
  const [resetPw,    setResetPw]    = useState("");
  const [resetPw2,   setResetPw2]   = useState("");
  const [resetSent,  setResetSent]  = useState(false);

  // Phase-1 state
  const [p1User, setP1User] = useState("");
  const [p1Pass, setP1Pass] = useState("");

  // ---- Cognito handlers ----

  // Route a SignInResult to the right next step. Shared by the password sign-in
  // and the new-password challenge (which can itself lead to an MFA challenge).
  function routeResult(result: SignInResult) {
    if (result.status === "success") { onSignedIn(); return; }
    autoSubmittedCode.current = null;
    setCode("");
    if (result.status === "new_password_required") {
      setNewPw("");
      setNewPw2("");
      setStep({ kind: "new_password", cognitoUser: result.cognitoUser, email: email.trim() });
    } else if (result.status === "totp_required") {
      setStep({ kind: "totp", cognitoUser: result.cognitoUser });
    } else {
      // mfa_setup
      setStep({ kind: "setup", cognitoUser: result.cognitoUser, secretCode: result.secretCode, email: email.trim() });
    }
  }

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      routeResult(await signIn(email.trim(), password));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (step.kind !== "new_password") return;
    if (newPw.length < 12) { setError("Password must be at least 12 characters."); return; }
    if (newPw !== newPw2)   { setError("Passwords do not match."); return; }
    setBusy(true);
    setError(null);
    try {
      routeResult(await completeNewPassword(step.cognitoUser, newPw));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleTotp(e?: React.FormEvent) {
    e?.preventDefault();
    if (step.kind !== "totp") return;
    setBusy(true);
    setError(null);
    try {
      await respondToTotp(step.cognitoUser, code.trim());
      onSignedIn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSetup(e?: React.FormEvent) {
    e?.preventDefault();
    if (step.kind !== "setup") return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotpSetup(step.cognitoUser, code.trim());
      onSignedIn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function backToCredentials() {
    setStep({ kind: "credentials" });
    setCode("");
    setNewPw("");
    setNewPw2("");
    setError(null);
    autoSubmittedCode.current = null;
  }

  function goToForgotPassword() {
    setResetEmail(email.trim());
    setResetCode("");
    setResetPw("");
    setResetPw2("");
    setResetSent(false);
    setError(null);
    setStep({ kind: "forgot_password" });
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!resetEmail.trim()) { setError("Enter your email."); return; }
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(resetEmail.trim());
      setStep({ kind: "reset_password", email: resetEmail.trim() });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (step.kind !== "reset_password") return;
    if (!resetCode.trim()) { setError("Enter the code sent to your email."); return; }
    if (resetPw.length < 12) { setError("Password must be at least 12 characters."); return; }
    if (resetPw !== resetPw2) { setError("Passwords do not match."); return; }
    setBusy(true);
    setError(null);
    try {
      await confirmForgotPassword(step.email, resetCode.trim(), resetPw);
      setResetSent(true);
      setEmail(step.email);
      setPassword("");
      setStep({ kind: "credentials" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleResendCode() {
    if (step.kind !== "reset_password") return;
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(step.email);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Auto-submit the MFA code the moment a full 6-digit code is entered, so the
  // user doesn't have to reach for the Verify/Activate button. Guarded so we
  // attempt a given code at most once (a wrong code stays on screen at length 6;
  // editing it to a new 6-digit value re-arms the auto-submit).
  const autoSubmittedCode = useRef<string | null>(null);
  useEffect(() => {
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) return;
    if (busy || autoSubmittedCode.current === trimmed) return;
    if (step.kind === "totp") {
      autoSubmittedCode.current = trimmed;
      void handleTotp();
    } else if (step.kind === "setup") {
      autoSubmittedCode.current = trimmed;
      void handleSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, busy, step.kind]);

  // ---- Phase-1 handler ----

  async function submitPhase1(e: React.FormEvent) {
    e.preventDefault();
    if (!p1User.trim() || !p1Pass) { setError("Enter your username and password."); return; }
    setBusy(true);
    setError(null);
    try {
      await login(p1User.trim(), p1Pass);
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  // ---- Render ----

  if (!cognito) {
    // Phase-1 fallback: plain username + password form.
    return (
      <Card>
        <form onSubmit={submitPhase1} className="space-y-5">
          <Heading title="Medical Assistant" subtitle="Sign in to continue." />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
            <ClearableInput type="text" value={p1User} onChange={(e) => setP1User(e.target.value)}
              autoFocus autoComplete="username" className={inputCls} />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
            <ClearableInput type="password" value={p1Pass} onChange={(e) => setP1Pass(e.target.value)}
              autoComplete="current-password" className={inputCls} />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </Card>
    );
  }

  // ---- Step: email + password ----

  if (step.kind === "credentials") {
    return (
      <Card>
        <form onSubmit={handleCredentials} className="space-y-5">
          <Heading title="Medical Assistant" subtitle="Sign in with your account." />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <ClearableInput type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              autoFocus autoComplete="username email" className={inputCls} />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
              <button type="button" onClick={goToForgotPassword} className={btnGhost}>
                Forgot password?
              </button>
            </div>
            <ClearableInput type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password" className={inputCls} />
          </div>

          {resetSent && !error && (
            <p className="text-sm text-green-600 dark:text-green-400">
              Password reset. Sign in with your new password.
            </p>
          )}

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <p className="text-center text-xs text-gray-400 dark:text-gray-600">
            Access is by invitation only. Contact the administrator to request access.
          </p>
        </form>
      </Card>
    );
  }

  // ---- Step: set a new password (first sign-in from an invite) ----

  if (step.kind === "new_password") {
    return (
      <Card>
        <form onSubmit={handleNewPassword} className="space-y-5">
          <Heading title="Set Your Password" subtitle="Choose a permanent password to finish setting up your account." />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
            <ClearableInput type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              autoFocus autoComplete="new-password" className={inputCls} />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm password</label>
            <ClearableInput type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
              autoComplete="new-password" className={inputCls} />
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters, with uppercase, lowercase, a number, and a symbol.
          </p>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Saving…" : "Set password & continue"}
          </button>

          <div className="text-center">
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  // ---- Step: forgot password (request a reset code) ----

  if (step.kind === "forgot_password") {
    return (
      <Card>
        <form onSubmit={handleForgotPassword} className="space-y-5">
          <Heading title="Reset Your Password" subtitle="Enter your email and we'll send you a reset code." />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <ClearableInput type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
              autoFocus autoComplete="username email" className={inputCls} />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Sending…" : "Send reset code"}
          </button>

          <div className="text-center">
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  // ---- Step: reset password (enter emailed code + new password) ----

  if (step.kind === "reset_password") {
    return (
      <Card>
        <form onSubmit={handleResetPassword} className="space-y-5">
          <Heading title="Enter Reset Code" subtitle={`We sent a code to ${step.email}.`} />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reset code</label>
            <ClearableInput type="text" inputMode="numeric" value={resetCode} onChange={(e) => setResetCode(e.target.value)}
              autoFocus autoComplete="one-time-code" placeholder="000000" className={inputCls} />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
            <ClearableInput type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)}
              autoComplete="new-password" className={inputCls} />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm password</label>
            <ClearableInput type="password" value={resetPw2} onChange={(e) => setResetPw2(e.target.value)}
              autoComplete="new-password" className={inputCls} />
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters, with uppercase, lowercase, a number, and a symbol.
          </p>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Resetting…" : "Reset password"}
          </button>

          <div className="flex items-center justify-between">
            <button type="button" onClick={handleResendCode} disabled={busy} className={btnGhost}>
              Resend code
            </button>
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  // ---- Step: TOTP challenge (user has MFA configured) ----

  if (step.kind === "totp") {
    return (
      <Card>
        <form onSubmit={handleTotp} className="space-y-5">
          <Heading title="Two-Factor Authentication" subtitle="Enter the 6-digit code from your authenticator app." />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Authentication code</label>
            <ClearableInput type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
              ref={(el) => { if (el) el.focus(); }}
              value={code} onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code" placeholder="000000" className={inputCls} />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button type="submit" disabled={busy || code.trim().length !== 6} className={btnPrimary}>
            {busy ? "Verifying…" : "Verify"}
          </button>

          <div className="text-center">
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  // ---- Step: TOTP setup (first-time MFA enrollment) ----

  const { secretCode, email: setupEmail } = step;
  const uri = totpUri(setupEmail, secretCode);

  return (
    <Card wide>
      <form onSubmit={handleSetup} className="space-y-5">
        <Heading
          title="Set Up Two-Factor Authentication"
          subtitle="Scan the QR code with Google Authenticator, Authy, or any TOTP app."
        />

        {/* QR code — always white background so it scans in dark mode too */}
        <div className="flex justify-center">
          <div className="rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white p-3">
            <QRCodeSVG value={uri} size={192} level="M" />
          </div>
        </div>

        {/* Collapsible manual-entry fallback */}
        <details className="rounded-md border border-gray-200 dark:border-gray-700 text-sm">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
            Can't scan? Enter the setup key manually ▸
          </summary>
          <div className="space-y-2 px-3 pb-3 pt-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              In your app choose <strong>"Enter setup key"</strong> and paste the key below.
              Account name: <em>Medical Assistant</em> · Type: <em>Time based</em>
            </p>
            <div className="rounded bg-gray-100 dark:bg-gray-800 px-3 py-2">
              <span className="block break-all select-all font-mono text-xs text-gray-900 dark:text-gray-100">
                {secretCode}
              </span>
            </div>
          </div>
        </details>

        {/* Mobile deep-link (does nothing on desktop — normal for otpauth://) */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-600">
          On a phone?{" "}
          <a href={uri} className="text-blue-600 dark:text-blue-400 underline">
            Tap to open in your authenticator app
          </a>
        </p>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Verification code (from your app)
          </label>
          <ClearableInput type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value)}
            autoFocus autoComplete="one-time-code" placeholder="000000" className={inputCls} />
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <button type="submit" disabled={busy || code.trim().length !== 6} className={btnPrimary}>
          {busy ? "Activating…" : "Activate & Sign in"}
        </button>

        <div className="text-center">
          <button type="button" onClick={backToCredentials} className={btnGhost}>
            ← Back to sign in
          </button>
        </div>
      </form>
    </Card>
  );
}

